/* Instant reads: an in-memory cache of API GET answers that is never wrong.
 *
 * The server announces every write on its change stream (/api/sync/events/,
 * see src/realtime.js; on the desktop app the local engine relays it). So a
 * GET answer stays exact until the next announcement: while nothing changed,
 * opening a page again is served from memory — no request, no spinner.
 *
 * - Any change announcement or any write sent from this app (POST/PUT/PATCH/
 *   DELETE) invalidates everything at once (one generation counter).
 * - Without a live change stream (offline, old desktop engine, stream down)
 *   answers are only reused for a few seconds (merges bursts of identical
 *   requests), so nothing can stay stale.
 * - Identical GETs in flight at the same time share one network request.
 * - After an invalidation the answers used in the last minutes are refetched
 *   in the background, so the next page opening is instant again.
 * - peekCached() gives the last good answer even when it is outdated: pages
 *   paint it at once and replace it when the fresh one arrives.
 *
 * Works at the adapter level: the cached value is the raw body, so every
 * caller gets its own freshly parsed copy (no shared mutable arrays).
 */
import axios from 'axios';
import { apiClient } from './authService';

const LIVE_MAX_AGE_MS = 3 * 60 * 1000;   // bound for time-based data (promos, "today")
const BLIND_MAX_AGE_MS = 8 * 1000;       // no change stream: only merge bursts
const MAX_ENTRIES = 250;
const HOT_WINDOW_MS = 10 * 60 * 1000;    // what counts as "recently used"
const REWARM_MAX = 14;
const REWARM_DELAY_MS = 600;
const ACTOR_HEADER = 'x-kipara-actor';

let generation = 0;
let liveSources = 0;          // connected change streams (web SSE / engine)
let lastActor = '';
const entries = new Map();    // key -> { raw, status, statusText, headers, gen, at, usedAt, auth, cfg, adapter }
const inflight = new Map();   // key -> Promise<response>
const ownChanges = new Set(); // change ids announced for writes made by this app

function header(cfg, name) {
  const h = cfg && cfg.headers;
  if (!h) return '';
  if (typeof h.get === 'function') return h.get(name) || '';
  const lower = name.toLowerCase();
  for (const k of Object.keys(h)) if (k.toLowerCase() === lower) return h[k] || '';
  return '';
}

function currentAuth() {
  try {
    const t = localStorage.getItem('token');
    return t ? `Token ${t}` : '';
  } catch {
    return '';
  }
}

function uriOf(client, cfg) {
  try {
    return client.getUri(cfg);
  } catch {
    return String(cfg.url || '');
  }
}

function cacheable(cfg, uri) {
  if ((cfg.method || 'get').toLowerCase() !== 'get') return false;
  if (cfg.cache === false) return false;
  if (cfg.responseType && cfg.responseType !== 'json' && cfg.responseType !== 'text') return false;
  if (!uri.includes('/api/')) return false;
  if (/\/api\/(sync|auth)\//.test(uri)) return false;
  return true;
}

const keyOf = (uri, auth, actor) => `${uri}|${auth}|${actor}`;

function isFresh(e) {
  if (e.gen !== generation) return false;
  const age = Date.now() - e.at;
  return age < (liveSources > 0 ? LIVE_MAX_AGE_MS : BLIND_MAX_AGE_MS);
}

function rawOf(data) {
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data);
  } catch {
    return null;
  }
}

function remember(key, res, gen, auth, cfg, adapter) {
  const raw = rawOf(res.data);
  if (raw == null) return;
  const now = Date.now();
  const prev = entries.get(key);
  entries.delete(key); // re-insert = most recent last (LRU order)
  entries.set(key, {
    raw,
    status: res.status,
    statusText: res.statusText,
    headers: res.headers,
    gen,
    at: now,
    usedAt: prev ? Math.max(prev.usedAt, now) : now,
    auth,
    cfg,
    adapter,
  });
  while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value);
}

function fromEntry(e, cfg) {
  return {
    data: e.raw,
    status: e.status,
    statusText: e.statusText,
    headers: e.headers,
    config: cfg,
    request: { fromCache: true },
  };
}

function fetchInto(key, cfg, adapter, auth) {
  const gen = generation;
  const p = Promise.resolve()
    .then(() => adapter(cfg))
    .then((res) => {
      if (res && res.status >= 200 && res.status < 300) {
        // Answered after a change was announced: still usable for peeking,
        // never served as fresh.
        remember(key, res, gen === generation ? gen : -1, auth, cfg, adapter);
      }
      return res;
    })
    .finally(() => {
      if (inflight.get(key) === p) inflight.delete(key);
    });
  inflight.set(key, p);
  // Each caller gets its own response object: axios parses `data` in place.
  return p.then((res) => ({ ...res, config: cfg }));
}

function resolveAdapter(cfg) {
  if (typeof cfg.adapter === 'function') return cfg.adapter;
  if (typeof axios.getAdapter === 'function') return axios.getAdapter(cfg.adapter || axios.defaults.adapter);
  return null;
}

function install(client) {
  if (client.__kiparaCache) return;
  client.__kiparaCache = true;

  // Request interceptors run last-registered-first, so the auth / actor headers
  // are only complete when the adapter runs: the cache key is built there.
  client.interceptors.request.use((cfg) => {
    if ((cfg.method || 'get').toLowerCase() !== 'get' || cfg.cache === false) return cfg;
    const real = resolveAdapter(cfg);
    if (!real) return cfg;
    cfg.adapter = (c) => {
      const uri = uriOf(client, c);
      if (!cacheable(c, uri)) return real(c);
      const auth = header(c, 'Authorization');
      const actor = header(c, ACTOR_HEADER);
      lastActor = actor;
      const key = keyOf(uri, auth, actor);
      const e = entries.get(key);
      if (e && isFresh(e)) {
        e.usedAt = Date.now();
        return Promise.resolve(fromEntry(e, c));
      }
      const pending = inflight.get(key);
      if (pending) return pending.then((res) => ({ ...res, config: c }));
      return fetchInto(key, c, real, auth);
    };
    return cfg;
  });

  // Any write sent from here changes server data: drop every cached answer.
  // The server tags the change it announces (header X-Kipara-Change): its
  // echo on the change stream is then recognised and not applied twice.
  const afterWrite = (cfg, res) => {
    const m = ((cfg && cfg.method) || 'get').toLowerCase();
    if (m === 'get' || m === 'head' || m === 'options') return;
    const id = res && res.headers && (typeof res.headers.get === 'function'
      ? res.headers.get('x-kipara-change') : res.headers['x-kipara-change']);
    if (id) {
      ownChanges.add(String(id));
      if (ownChanges.size > 100) ownChanges.delete(ownChanges.values().next().value);
    }
    invalidateApiCache();
  };
  client.interceptors.response.use(
    (res) => { afterWrite(res.config, res); return res; },
    (err) => { afterWrite(err && err.config, err && err.response); return Promise.reject(err); },
  );
}

// ------------------------------------------------------------ background rewarm
let rewarmTimer = null;
let rewarmRunning = false;
let rewarmAgain = false;

function scheduleRewarm() {
  clearTimeout(rewarmTimer);
  rewarmTimer = setTimeout(runRewarm, REWARM_DELAY_MS);
}

async function runRewarm() {
  if (rewarmRunning) { rewarmAgain = true; return; }
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    rewarmAgain = true; // done when the app comes back to the foreground
    return;
  }
  rewarmRunning = true;
  rewarmAgain = false;
  try {
    const auth = currentAuth();
    const now = Date.now();
    const hot = [...entries.entries()]
      .filter(([, e]) => e.gen !== generation && e.auth === auth && now - e.usedAt < HOT_WINDOW_MS)
      .sort((a, b) => b[1].usedAt - a[1].usedAt)
      .slice(0, REWARM_MAX);
    for (const [key, e] of hot) {
      if (rewarmAgain) break; // another change arrived: start over with fresh data
      const cur = entries.get(key);
      if (!cur || cur.gen === generation) continue;
      // The page that first asked may be gone: never inherit its abort signal.
      const cfg = { ...e.cfg, signal: undefined, cancelToken: undefined };
      const pending = inflight.get(key) || fetchInto(key, cfg, e.adapter, e.auth);
      try { await pending; } catch { /* next page opening will retry */ }
    }
  } finally {
    rewarmRunning = false;
    if (rewarmAgain) scheduleRewarm();
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && rewarmAgain) scheduleRewarm();
  });
}

// ------------------------------------------------------------------ public API
/** Something changed on the server (or here): nothing cached is exact anymore. */
export function invalidateApiCache() {
  generation += 1;
  scheduleRewarm();
}

/** True when a change-stream message is the echo of a write made here (the
 *  cache was already dropped when that write returned). */
export function isOwnChange(id) {
  return !!id && ownChanges.has(String(id));
}

/** A change stream connected (true) or dropped (false). */
export function setChangeStreamLive(isLive) {
  liveSources = Math.max(0, liveSources + (isLive ? 1 : -1));
}

/** Forget everything (logout / account switch). */
export function clearApiCache() {
  generation += 1;
  entries.clear();
  inflight.clear();
}

/**
 * Last good answer for `url` (relative to the API base, e.g. 'products/?page_size=5000'),
 * parsed, or undefined — even if outdated. For painting a page instantly.
 */
export function peekCached(url, params) {
  const uri = uriOf(apiClient, { url, params });
  const e = entries.get(keyOf(uri, currentAuth(), lastActor)) || entries.get(keyOf(uri, currentAuth(), ''));
  if (!e) return undefined;
  try {
    return JSON.parse(e.raw);
  } catch {
    return undefined;
  }
}

install(apiClient);
install(axios); // réservations, soins, catégories clients call axios directly

if (typeof window !== 'undefined') {
  window.addEventListener('kipara:login', clearApiCache);
  window.addEventListener('storage', (ev) => { if (ev.key === 'token') clearApiCache(); });
}
